<template>
  <div
    class="agreement-document-header"
    :class="{ 'agreement-document-header--compact': compact }"
  >
    <div class="agreement-document-header__side">
      <div class="agreement-document-header__logo-frame">
        <img
          :src="schoolLogo"
          alt="Mae Fah Luang University logo"
          class="agreement-document-header__logo"
        >
      </div>
    </div>


    <div class="agreement-document-header__title">
      <div class="agreement-document-header__eyebrow">{{ titleLabel }}</div>
      <strong>{{ safeTitle }}</strong>
      <small v-if="showMeta && metaText">{{ metaText }}</small>
    </div>

    <div class="agreement-document-header__side agreement-document-header__side--partner">
      <div class="agreement-document-header__logo-frame">
        <img
          v-if="partnerLogoUrl"
          :src="partnerLogoUrl"
          :alt="partnerAlt"
          class="agreement-document-header__logo agreement-document-header__logo--partner"
        >
        <div
          v-else
          class="agreement-document-header__logo agreement-document-header__logo--placeholder"
        >
          {{ partnerInitials }}
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import schoolLogo from '@/assets/mfu_logo.png'

export default {
  name: 'AgreementDocumentHeader',
  props: {
    title: {
      type: String,
      default: ''
    },
    partner: {
      type: String,
      default: ''
    },
    partnerLogo: {
      type: [Object, String],
      default: null
    },
    schoolName: {
      type: String,
      default: 'Mae Fah Luang University'
    },
    titleLabel: {
      type: String,
      default: 'Agreement Title'
    },
    agreementType: {
      type: String,
      default: ''
    },
    country: {
      type: String,
      default: ''
    },
    compact: {
      type: Boolean,
      default: false
    },
    showMeta: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      schoolLogo
    }
  },
  computed: {
    safeTitle() {
      return String(this.title || '').trim() || 'Untitled Agreement'
    },
    safePartner() {
      return String(this.partner || '').trim() || 'Partner Institution'
    },
    partnerLogoUrl() {
      if (!this.partnerLogo) return ''
      if (typeof this.partnerLogo === 'string') return this.partnerLogo
      return this.partnerLogo && this.partnerLogo.url ? this.partnerLogo.url : ''
    },
    partnerAlt() {
      return `${this.safePartner} logo`
    },
    partnerInitials() {
      return this.safePartner
        .split(/\s+/)
        .slice(0, 2)
        .map((item) => item.charAt(0).toUpperCase())
        .join('') || 'PI'
    },
    metaText() {
      return [this.agreementType, this.country].filter(Boolean).join(' · ')
    }
  }
}
</script>

<style scoped>
.agreement-document-header {
  align-items: center;
  background: linear-gradient(180deg, #fff8f4 0%, #fff 100%);
  border: 1px solid #f2e8df;
  border-radius: 22px;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 180px) minmax(0, 1fr) minmax(0, 180px);
  padding: 20px 22px;
}

.agreement-document-header--compact {
  border-radius: 18px;
  gap: 14px;
  grid-template-columns: minmax(0, 150px) minmax(0, 1fr) minmax(0, 150px);
  padding: 16px 18px;
}

.agreement-document-header__side {
  align-items: center;
  color: #475569;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  text-align: center;
}

.agreement-document-header__title {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  text-align: center;
}

.agreement-document-header__eyebrow {
  color: #9e212f;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.agreement-document-header__title strong {
  color: #1f2937;
  font-size: 1.2rem;
  line-height: 1.35;
}

.agreement-document-header__title small {
  color: #64748b;
  font-size: 0.84rem;
}

.agreement-document-header__logo-frame {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  display: flex;
  height: 84px;
  justify-content: center;
  overflow: hidden;
  padding: 10px;
  width: 84px;
}

.agreement-document-header--compact .agreement-document-header__logo-frame {
  border-radius: 18px;
  height: 72px;
  width: 72px;
}

.agreement-document-header__logo {
  height: 100%;
  max-width: 100%;
  object-fit: contain;
  width: 100%;
}

.agreement-document-header__logo--partner {
  object-fit: cover;
}

.agreement-document-header__logo--placeholder {
  align-items: center;
  background: linear-gradient(135deg, #9e212f 0%, #b94852 100%);
  color: #fff;
  display: flex;
  font-size: 1rem;
  font-weight: 700;
  justify-content: center;
}

.agreement-document-header__side {
  /* keep spacing for logo-only layout */
  gap: 0;
}


@media (max-width: 900px) {
  .agreement-document-header,
  .agreement-document-header--compact {
    grid-template-columns: 1fr;
    padding: 18px;
  }
}
</style>
